import { useCallback, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { clsx } from "clsx"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import {
  CONTRACT_PREPARATION_SUPERSEDED_MESSAGE,
  isContractPreparationSupersededError,
} from "~/api/battery-contract-errors"
import { USER_KEYS } from "~/api/cache"
import type {
  ContractBlockerCodeOf,
  ContractBlockerDto,
  ContractHistoryEntryDto,
  ContractHistoryStatus,
  ProposalContractReadinessDto,
} from "~/api/dto/proposal-contract.dto"
import { userService } from "~/api/services/user.service"
import { Drawer } from "~/components/ui-components/v2"

import { ContractFixStepLink } from "./contract-fix-actions"
import {
  contractHistoryLabel,
  contractPackageLabel,
  type ContractKindBundle,
} from "./contract-kind-bundle"
import { Eyebrow, Icon } from "../../BatteryOnlyProposal/primitives"
import { formatUsdExact } from "../../BatteryOnlyProposal/view-helpers"

export const READY_HEADLINE = `Ready to send`
export const NOT_READY_HEADLINE = `Not ready to send`
export const RECONCILING_HEADLINE = `Reconciling send`
export const VIEW_ONLY_NOTE = `You have view-only access to this proposal.`
export const SUPPORT_BLOCKER_MESSAGE = `This proposal needs a change only Powur can make. Support can see the details; nothing on the proposal needs to change.`

type Blocker<R extends ProposalContractReadinessDto> = ContractBlockerDto<
  ContractBlockerCodeOf<R>
>

const HISTORY_STATUS_LABEL: Record<ContractHistoryStatus, string> = {
  awaiting_signature: `Awaiting signature`,
  prepared: `Preparing`,
  signed: `Signed`,
  send_unknown: `Reconciling`,
  send_failed: `Send failed`,
  sending: `Sending`,
  superseded: `Superseded`,
  voided: `Voided`,
  declined: `Declined`,
  expired: `Expired`,
}

const CLOSED_STATUSES = new Set<ContractHistoryStatus>([
  `voided`,
  `declined`,
  `expired`,
])

/**
 * A blocker the seller can act on from here. Drives the chip copy so it never
 * promises agency the drawer does not offer.
 */
const hasFixPath = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  proposalId: string,
  blocker: Blocker<R>,
  readiness: R,
): boolean => {
  const inline = bundle.inlineFixes[blocker.code]
  return inline
    ? inline.available(readiness)
    : bundle.fixStep(proposalId, blocker.code) != null &&
        readiness.actions.viewerCanUpdate
}

const ownerLabel = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  proposalId: string,
  blocker: Blocker<R>,
  readiness: R,
): string =>
  blocker.owner === `system`
    ? `Nothing to do here`
    : hasFixPath(bundle, proposalId, blocker, readiness)
      ? `You can fix this`
      : `Needs your attention`

/**
 * Support-owned blockers name templates, signer roles and provider failures.
 * Sellers cannot act on any of them and the wording is internal, so they
 * collapse to one line; the server keeps the detail for operators.
 */
type BlockerRow<B extends ContractBlockerDto> =
  | { kind: `blocker`; blocker: B }
  | { kind: `support` }

const blockerRows = <R extends ProposalContractReadinessDto>(
  readiness: R,
): BlockerRow<Blocker<R>>[] => {
  const rows: BlockerRow<Blocker<R>>[] = readiness.blockers
    .filter((blocker) => blocker.owner !== `support`)
    .map((blocker) => ({ kind: `blocker`, blocker }))
  return readiness.blockers.some((blocker) => blocker.owner === `support`)
    ? [...rows, { kind: `support` }]
    : rows
}

const STAGES = [`Prepare`, `Sent`, `Signed`] as const

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat(`en-US`, {
    month: `short`,
    day: `numeric`,
    year: `numeric`,
  }).format(new Date(iso))

const latestOf = (
  readiness: ProposalContractReadinessDto,
): ContractHistoryEntryDto | null => {
  const primaryHistory = readiness.history
    .filter((entry) => entry.contractType === `primary`)
    .sort((a, b) => a.version - b.version)
  return primaryHistory[primaryHistory.length - 1] ?? null
}

const stageOf = (readiness: ProposalContractReadinessDto): 0 | 1 | 2 => {
  const latest = latestOf(readiness)
  if (latest?.status === `signed`) return 2
  if (
    latest?.status === `awaiting_signature` ||
    latest?.status === `send_unknown` ||
    latest?.status === `sending`
  ) {
    return 1
  }
  return 0
}

const headlineOf = <R extends ProposalContractReadinessDto>(
  bundle: ContractKindBundle<R>,
  readiness: R,
): { title: string; detail: string } => {
  const latest = latestOf(readiness)
  if (latest?.status === `signed`) {
    return {
      title: `Signed`,
      detail: latest.dateSigned
        ? `Contract v${latest.version} completed ${formatDate(
            latest.dateSigned,
          )}.`
        : `Contract v${latest.version} is complete.`,
    }
  }
  if (latest?.status === `awaiting_signature`) {
    return {
      title: `Awaiting signature`,
      detail: latest.dateSent
        ? `Sent ${formatDate(latest.dateSent)}.`
        : `Sent to DocuSign.`,
    }
  }
  if (latest?.status === `send_unknown` || latest?.status === `sending`) {
    return {
      title: RECONCILING_HEADLINE,
      detail: `Vision is matching the reserved contract to DocuSign. Another send is unavailable until that outcome is known.`,
    }
  }
  if (latest?.status === `send_failed`) {
    return {
      title: NOT_READY_HEADLINE,
      detail: `The last send failed before DocuSign accepted a package. You can try again.`,
    }
  }
  if (latest?.status === `superseded`) {
    return {
      title: NOT_READY_HEADLINE,
      detail: `Contract preparation was superseded by a proposal change. Reload readiness and send again.`,
    }
  }
  if (latest && CLOSED_STATUSES.has(latest.status)) {
    const closedOn = latest.dateClosed
      ? formatDate(latest.dateClosed)
      : latest.dateSent
        ? formatDate(latest.dateSent)
        : null
    return {
      title: HISTORY_STATUS_LABEL[latest.status],
      detail: closedOn
        ? `Contract v${latest.version} ${latest.status} ${closedOn}. Send a new version when ready.`
        : `Contract v${latest.version} is ${latest.status}. Send a new version when ready.`,
    }
  }
  if (!readiness.flavor) {
    return { title: `Not available`, detail: readiness.eligibilityReason }
  }
  const rows = blockerRows(readiness)
  if (rows.length > 0) {
    return {
      title: NOT_READY_HEADLINE,
      detail:
        rows.length === 1
          ? `1 item needs attention.`
          : `${rows.length} items need attention.`,
    }
  }
  return {
    title: READY_HEADLINE,
    detail: `${contractPackageLabel(bundle, readiness, readiness.flavor)}${
      readiness.currentTotal != null
        ? ` for ${formatUsdExact(readiness.currentTotal)}`
        : ``
    }.`,
  }
}

const StatusPill = ({
  status,
}: {
  status: ContractHistoryStatus
}): JSX.Element => (
  <span
    className={clsx(
      `inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-medium`,
      status === `signed`
        ? `border-bp-success/40 bg-bp-success-soft text-bp-success`
        : CLOSED_STATUSES.has(status)
          ? `border-bp-danger/40 bg-bp-danger/10 text-bp-danger`
          : `border-bp-gold/40 bg-bp-gold/10 text-bp-gold`,
    )}
  >
    {HISTORY_STATUS_LABEL[status]}
  </span>
)

function BlockerFix<R extends ProposalContractReadinessDto>({
  bundle,
  proposalId,
  blocker,
  readiness,
  onFixSettled,
}: {
  bundle: ContractKindBundle<R>
  proposalId: string
  blocker: Blocker<R>
  readiness: R
  onFixSettled: () => void
}): JSX.Element | null {
  const inline = bundle.inlineFixes[blocker.code]
  if (inline) {
    return (
      <inline.Action
        proposalId={proposalId}
        readiness={readiness}
        onSettled={onFixSettled}
      />
    )
  }

  const step = bundle.fixStep(proposalId, blocker.code)
  if (!step || !readiness.actions.viewerCanUpdate) {
    return null
  }

  return <ContractFixStepLink step={step} />
}

function ContractContent<R extends ProposalContractReadinessDto>({
  bundle,
  proposalId,
  readiness,
  onFixSettled,
}: {
  bundle: ContractKindBundle<R>
  proposalId: string
  readiness: R
  onFixSettled: () => void
}): JSX.Element {
  const rows = blockerRows(readiness)
  const { data: profile } = useQuery({
    queryKey: USER_KEYS.profile(),
    queryFn: () => userService.getProfile(),
  })
  const isAdmin =
    profile?.permissions.some(
      (permission) => permission === `admin` || permission === `vision_admin`,
    ) ?? false
  const { LanguageField, PricingOutOfDateIndicator, ProviderMainContract } =
    bundle

  return (
    <div className="flex flex-1 flex-col gap-4">
      {ProviderMainContract ? (
        <ProviderMainContract proposalId={proposalId} readiness={readiness} />
      ) : null}
      {rows.length > 0 ? (
        <section
          aria-label="Needs attention"
          className="rounded-lg border border-bp-border bg-bp-elevated"
        >
          <h3 className="border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
            Needs attention
          </h3>
          <ul className="divide-y divide-bp-border">
            {rows.map((row) => (
              // Codes repeat per role; the message is unique per blocker.
              <li
                key={
                  row.kind === `support`
                    ? `support`
                    : `${row.blocker.code}:${row.blocker.message}`
                }
                className="flex gap-3 px-4 py-3"
              >
                <Icon name="warning" className="mt-0.5 size-4 text-bp-gold" />
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-[length:var(--step--1)] leading-snug text-bp-text">
                      {row.kind === `support`
                        ? SUPPORT_BLOCKER_MESSAGE
                        : row.blocker.message}
                    </p>
                    <span className="text-[length:var(--step--2)] font-semibold tracking-widest text-bp-text-muted uppercase">
                      {row.kind === `support`
                        ? `Needs support`
                        : ownerLabel(bundle, proposalId, row.blocker, readiness)}
                    </span>
                  </div>
                  {row.kind === `support` ? (
                    isAdmin ? (
                      <div className="flex flex-col gap-2 text-[length:var(--step--2)] text-bp-text">
                        {readiness.template ? (
                          <p>Template: {readiness.template.name}</p>
                        ) : null}
                        <ul className="list-disc pl-4">
                          {readiness.blockers
                            .filter((blocker) => blocker.owner === `support`)
                            .map((blocker) => (
                              <li key={`${blocker.code}:${blocker.message}`}>
                                {blocker.message}
                              </li>
                            ))}
                        </ul>
                        {readiness.blockers.some(
                          (blocker) => blocker.code === `SIGNING_ANCHOR_MISSING`,
                        ) ? (
                          <p>
                            Assign a signature or initial field to the affected
                            recipient in DocuSign, then reload contract
                            readiness.
                          </p>
                        ) : null}
                      </div>
                    ) : null
                  ) : (
                    <BlockerFix
                      bundle={bundle}
                      proposalId={proposalId}
                      blocker={row.blocker}
                      readiness={readiness}
                      onFixSettled={onFixSettled}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section
        aria-label="Package"
        className="rounded-lg border border-bp-border bg-bp-elevated"
      >
        <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
          <h3 className="text-[length:var(--step--1)] font-semibold text-bp-text">
            Package
          </h3>
          <span className="flex items-center gap-2 text-[length:var(--step--1)] font-semibold text-bp-text tabular-nums">
            {readiness.currentTotal != null
              ? formatUsdExact(readiness.currentTotal)
              : `—`}
            {readiness.pricingStatus === `out_of_date` ? (
              <PricingOutOfDateIndicator />
            ) : null}
          </span>
        </div>
        <div className="flex flex-col gap-4 px-4 py-4">
          <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-[length:var(--step--2)]">
            <dt className="text-bp-text-dim">Contract</dt>
            <dd className="text-bp-text">
              {readiness.flavor
                ? contractPackageLabel(bundle, readiness, readiness.flavor)
                : `Not available`}
            </dd>
            <dt className="text-bp-text-dim">Why</dt>
            <dd className="text-bp-text">{readiness.eligibilityReason}</dd>
            <dt className="text-bp-text-dim">Language</dt>
            <dd className="text-bp-text">
              <LanguageField proposalId={proposalId} readiness={readiness} />
            </dd>
          </dl>
          {readiness.recipients.length > 0 ? (
            <ol className="flex flex-col gap-2" aria-label="Signers">
              {readiness.recipients.map((recipient) => (
                <li
                  key={recipient.role}
                  className="flex min-w-0 items-center gap-3"
                >
                  <span className="font-display grid size-7 shrink-0 place-items-center rounded-full border border-bp-border-strong bg-bp-elevated-2 text-[length:var(--step--2)] font-bold text-bp-text-muted">
                    {recipient.routingOrder}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[length:var(--step--1)] font-semibold text-bp-text">
                      {recipient.name}
                      <span className="ml-2 font-normal text-bp-text-dim">
                        {recipient.role}
                        {recipient.source === `mentor` ? ` · mentor` : ``}
                      </span>
                    </div>
                    <div className="truncate text-[length:var(--step--2)] text-bp-text-muted">
                      {recipient.email}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </section>

      <section
        aria-label="History"
        className="rounded-lg border border-bp-border bg-bp-elevated"
      >
        <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
          <h3 className="text-[length:var(--step--1)] font-semibold text-bp-text">
            History
          </h3>
          <span className="text-[length:var(--step--2)] text-bp-text-muted">
            {readiness.history.length === 0
              ? `Nothing sent`
              : `${readiness.history.length} version${
                  readiness.history.length === 1 ? `` : `s`
                }`}
          </span>
        </div>
        <ul className="divide-y divide-bp-border">
          {readiness.history.length === 0 ? (
            <li className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
              Sending creates version 1.
            </li>
          ) : (
            [...readiness.history].reverse().map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="text-[length:var(--step--2)] text-bp-text-muted">
                  <span className="font-semibold text-bp-text">
                    Version {entry.version}
                  </span>
                  {entry.flavor
                    ? ` · ${contractHistoryLabel(bundle, entry.sentAsSupplemental, entry.flavor)}`
                    : ``}
                  {entry.dateSent ? ` · sent ${formatDate(entry.dateSent)}` : ``}
                  {entry.dateSigned
                    ? ` · signed ${formatDate(entry.dateSigned)}`
                    : ``}
                  {entry.dateClosed && CLOSED_STATUSES.has(entry.status)
                    ? ` · ${entry.status} ${formatDate(entry.dateClosed)}`
                    : ``}
                </div>
                <StatusPill status={entry.status} />
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  )
}

/**
 * A proposal's Contract drawer, routed as a sibling of its Setup drawers at
 * `/<kind>-proposal/:proposalId/contract`. Renders server-derived readiness
 * only: flavor, recipients, stored total, blockers and history. Send controls
 * appear only for viewers the server marks as able to update. A kind's
 * partner-run status drawer can take the route instead.
 */
export function ContractDrawer<R extends ProposalContractReadinessDto>({
  bundle,
}: {
  bundle: ContractKindBundle<R>
}): JSX.Element | null {
  const { proposalId } = useParams<{ proposalId: string }>()
  const navigate = useNavigate()
  const { state } = useLocation()
  const PartnerOverride = bundle.usePartnerOverride(proposalId)

  const onClose = useCallback(() => {
    if (proposalId) {
      navigate(bundle.returnTo(state, bundle.proposalPath(proposalId)))
    }
  }, [bundle, navigate, proposalId, state])

  if (!proposalId) {
    return null
  }

  if (PartnerOverride) {
    return <PartnerOverride proposalId={proposalId} onClose={onClose} />
  }

  return (
    <VisionContractDrawer
      bundle={bundle}
      proposalId={proposalId}
      onClose={onClose}
    />
  )
}

/** Every Finance Plan whose contract Vision itself prepares and sends. */
function VisionContractDrawer<R extends ProposalContractReadinessDto>({
  bundle,
  proposalId,
  onClose,
}: {
  bundle: ContractKindBundle<R>
  proposalId: string
  onClose: () => void
}): JSX.Element {
  const query = bundle.useReadiness(proposalId)
  const send = bundle.useSend()
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID())

  const readiness = query.data
  const headline = readiness ? headlineOf(bundle, readiness) : null
  const stage = readiness ? stageOf(readiness) : 0
  const showSendControls = readiness?.actions.viewerCanUpdate === true
  const sendErrorMessage = send.isError
    ? isContractPreparationSupersededError(send.error)
      ? CONTRACT_PREPARATION_SUPERSEDED_MESSAGE
      : `Unable to send documents`
    : null

  // Joins a read the fix's own invalidation already started.
  const refetchReadiness = () => {
    void query.refetch({ cancelRefetch: false })
  }

  const onSend = () => {
    send.mutate(
      { proposalId, idempotencyKey },
      {
        onSettled: () => {
          // Each attempt consumes the key (success, definitive failure, or
          // superseded). Rotate so the next Send documents click can retry.
          setIdempotencyKey(crypto.randomUUID())
        },
        onError: (error) => {
          if (isContractPreparationSupersededError(error)) {
            void query.refetch()
          }
        },
      },
    )
  }

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title="Contract"
      widthClass="sm:max-w-xl"
      hideCloseButton
      contentClassName="flex flex-col pb-0"
      header={({ close }) => (
        <div className="border-b border-bp-border bg-bp-base px-6 pt-5 pb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow className="mb-1">
                {readiness?.flavor
                  ? contractPackageLabel(bundle, readiness, readiness.flavor)
                  : `Contract`}
              </Eyebrow>
              <h2 className="font-display text-[length:var(--step-2)] font-semibold text-bp-text">
                {headline?.title ??
                  (query.isError
                    ? `Unable to load contract readiness`
                    : `Checking readiness`)}
              </h2>
              {headline ? (
                <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
                  {headline.detail}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={close}
              className="rounded-md px-2 py-1 text-[length:var(--step--2)] font-semibold text-bp-text-muted hover:text-bp-text"
            >
              Close
            </button>
          </div>
          <ol
            className="mt-5 grid grid-cols-3 gap-2"
            aria-label="Contract progress"
          >
            {STAGES.map((label, index) => (
              <li key={label} className="flex flex-col gap-1.5">
                <span
                  className={clsx(
                    `h-1 rounded-full`,
                    index < stage
                      ? `bg-bp-success`
                      : index === stage
                        ? `bg-bp-accent`
                        : `bg-bp-border-strong`,
                  )}
                />
                <span
                  className={clsx(
                    `text-[length:var(--step--2)] font-semibold`,
                    index === stage ? `text-bp-text` : `text-bp-text-dim`,
                  )}
                >
                  {label}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    >
      {readiness ? (
        <ContractContent
          bundle={bundle}
          proposalId={proposalId}
          readiness={readiness}
          onFixSettled={refetchReadiness}
        />
      ) : (
        <div
          role="status"
          className="flex-1 text-[length:var(--step--1)] text-bp-text-muted"
        >
          {query.isError
            ? `Contract readiness could not be loaded. Close and try again.`
            : `Checking the contract template, pricing and signers…`}
        </div>
      )}

      {readiness ? (
        <footer className="sticky bottom-0 -mx-4 mt-6 flex items-center justify-between gap-3 border-t border-bp-border bg-bp-page px-4 py-4 sm:-mx-6 sm:px-6">
          <p className="text-[length:var(--step--2)] text-bp-text-muted">
            {!readiness.actions.viewerCanUpdate
              ? VIEW_ONLY_NOTE
              : !showSendControls
                ? `Contract sending is not available yet.`
                : readiness.actions.canSend
                  ? `Sends one DocuSign envelope to ${readiness.recipients.length} signers.`
                  : `Send is disabled until everything above is resolved.`}
          </p>
          {showSendControls ? (
            <div className="flex flex-col items-end gap-2">
              {sendErrorMessage ? (
                <p
                  role="alert"
                  className="text-[length:var(--step--2)] text-bp-danger"
                >
                  {sendErrorMessage}
                </p>
              ) : null}
              <button
                type="button"
                disabled={!readiness.actions.canSend || send.isLoading}
                onClick={onSend}
                className={clsx(
                  `rounded-md px-5 py-2.5 text-[length:var(--step--1)] font-semibold`,
                  readiness.actions.canSend && !send.isLoading
                    ? `bg-bp-accent text-bp-on-accent hover:bg-bp-accent-hover`
                    : `cursor-not-allowed border border-bp-border-strong bg-bp-elevated text-bp-text-dim`,
                )}
              >
                {send.isLoading ? `Sending` : `Send documents`}
              </button>
            </div>
          ) : null}
        </footer>
      ) : null}
    </Drawer>
  )
}
